import { Link } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FilamentComparePanel from '../../../components/feature/FilamentComparePanel';
import { FilamentStressStrainChart } from '../../../components/feature/FilamentStressStrainChart';
import { useSEO } from '../../../hooks/useSEO';
import { useFilamentText } from '../../../hooks/useFilamentText';
import { useLocation } from 'react-router-dom';
import BendingStrengthCard from '../../../components/feature/BendingStrengthCard';

const HTPLAPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const TEXT = useFilamentText();
  const materialData = TEXT.materials?.['ht-pla'];

  useSEO({
    title: 'HT-PLA（高耐熱PLA）',
    description: isEnglish
      ? 'High heat resistance PLA over 100°C. Further enhanced with annealing. Maintains PLA ease of use while suitable for high-temperature environments.'
      : '耐熱温度100°C以上の高耐熱PLA。アニーリング処理で更に強化可能。PLAの扱いやすさを維持しながら高温環境での使用が可能。',
    keywords: '3Dプリンター,HT-PLA,高耐熱PLA,フィラメント,アニーリング,耐熱',
    canonicalPath: isEnglish ? '/en/filament/ht-pla' : '/filament/ht-pla'
  });

  const breadcrumbItems = isEnglish ? [
    { label: 'Home', path: '/en' },
    { label: 'Filament Data', path: '/filament' },
    { label: 'PLA', path: '/filament/pla' },
    { label: 'HT-PLA', path: '/en/filament/ht-pla' }
  ] : [
    { label: 'ホーム', path: '/' },
    { label: 'フィラメントデータ', path: '/filament' },
    { label: 'PLA', path: '/filament/pla' },
    { label: 'HT-PLA', path: '/filament/ht-pla' }
  ];

  const filamentId = 'ht-pla';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* タイトル */}
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                HT-PLA{isEnglish ? '' : '（高耐熱PLA）'}
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 比較指標 */}
            <FilamentComparePanel slug="ht-pla" />

            {/* 特徴 */}
            <div className="metal-panel p-8 border-l-4 border-accent">
              <h2 className="text-3xl font-semibold text-white font-['Exo'] mb-6 accent-line pb-3">
                {TEXT.features}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ul className="text-metal-light space-y-3 text-lg">
                  {materialData?.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-400 mt-1"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <ul className="text-metal-light space-y-3 text-lg">
                  {materialData?.features.slice(3).map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-400 mt-1"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 基本情報 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.basicInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.classification}</span>
                      <span className="difficulty-intermediate px-3 py-1 rounded text-sm font-semibold">{TEXT.intermediate}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.baseResin}</span>
                      <span className="text-white">{materialData?.baseResin}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.meltingPoint}</span>
                      <span className="text-white">160-170°C</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.density}</span>
                      <span className="text-white">1.24 g/cm³</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.shrinkageRate}</span>
                      <span className="text-white">0.4-0.6%</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.heatResistance}</span>
                      <span className="text-white">{isEnglish ? '100-110°C (after annealing)' : '100-110°C（アニーリング後）'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 推奨設定 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.recommendedSettings}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#3a3d42] text-white">
                      <th className="px-4 py-3 text-left">{TEXT.item}</th>
                      <th className="px-4 py-3 text-left">{TEXT.recommendedValue}</th>
                      <th className="px-4 py-3 text-left">{TEXT.notes}</th>
                    </tr>
                  </thead>
                  <tbody className="text-metal-light">
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleTemp}</td>
                      <td className="px-4 py-3 text-white">210-230°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Higher than standard PLA' : '通常PLAより高め'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.bedTemp}</td>
                      <td className="px-4 py-3 text-white">60-70°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'For adhesion' : '密着性確保のため'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.fanSetting}</td>
                      <td className="px-4 py-3 text-white">50-80%</td>
                      <td className="px-4 py-3">{isEnglish ? 'Prioritize layer adhesion' : '層間接着を優先'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.printSpeed}</td>
                      <td className="px-4 py-3 text-white">40-60mm/s</td>
                      <td className="px-4 py-3">{isEnglish ? 'Quality focus' : '品質重視'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleDiameter}</td>
                      <td className="px-4 py-3 text-white">0.4mm</td>
                      <td className="px-4 py-3">{isEnglish ? 'Standard setting' : '標準設定'}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">{isEnglish ? 'Post-processing' : '後処理'}</td>
                      <td className="px-4 py-3 text-white" colSpan={2}>{isEnglish ? 'Annealing recommended (80-100°C, 1-2 hours)' : 'アニーリング推奨（80-100°C、1-2時間）'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 曲げ強度（統合版） */}
            {/* 曲げ強度 */}
            <BendingStrengthCard slug={filamentId} isEnglish={isEnglish} />

            <FilamentStressStrainChart 
              filamentId={filamentId}
              filamentName="HT-PLA"
            />

            {/* アニーリング処理 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {isEnglish ? 'Annealing Process' : 'アニーリング処理'}
              </h2>
              <div className="space-y-4">
                <div className="bg-metal p-4 rounded">
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Process' : '処理方法'}</h3>
                  <ol className="text-metal-light space-y-2 list-decimal list-inside">
                    <li>{isEnglish ? 'Preheat oven to 80-100°C' : 'オーブンを80-100°Cに予熱する'}</li>
                    <li>{isEnglish ? 'Place print in heat-resistant container' : '造形物を耐熱容器に入れる'}</li>
                    <li>{isEnglish ? 'Heat for 1-2 hours' : '1-2時間加熱する'}</li>
                    <li>{isEnglish ? 'Cool slowly (avoid rapid cooling)' : 'ゆっくりと冷却する（急冷は避ける）'}</li>
                  </ol>
                </div>
                <div className="bg-metal p-4 rounded">
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Effects' : '効果'}</h3>
                  <ul className="text-metal-light space-y-1">
                    <li>• {isEnglish ? 'Heat resistance improves to over 100°C' : '耐熱温度が100°C以上に向上'}</li>
                    <li>• {isEnglish ? 'Crystallization progresses, strength improves' : '結晶化が進み強度が向上'}</li>
                    <li>• {isEnglish ? 'Dimensions shrink slightly (about 2-3%)' : '寸法が若干縮小（2-3%程度）'}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 使用例 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.useCases}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Suitable Applications' : '向いている造形'}</h3>
                  <ul className="text-metal-light space-y-1 text-sm">
                    {materialData?.suitableUses.map((use, idx) => (
                      <li key={idx}>• {use}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Pros & Cons' : '長所・短所'}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-green-400">{isEnglish ? 'Pros:' : '長所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.pros}</span>
                    </div>
                    <div>
                      <span className="text-red-400">{isEnglish ? 'Cons:' : '短所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.cons}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 注意事項 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.precautions}
              </h2>
              <div className="text-metal-light space-y-2">
                {materialData?.precautions.map((precaution, idx) => (
                  <p key={idx}>• {precaution}</p>
                ))}
              </div>
            </div>

            {/* 戻るリンク */}
            <div className="text-center mt-8">
              <Link 
                to={isEnglish ? "/filament/pla" : "/filament/pla"}
                className="inline-flex items-center space-x-2 text-accent hover:text-accent-orange transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                <span>{isEnglish ? 'Back to PLA page' : 'PLA詳細ページに戻る'}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default HTPLAPage;
